import { jsx } from "react/jsx-runtime";
import { c } from "react-compiler-runtime";
import { forwardRef, useLayoutEffect } from "react";
import { useInputElement, useInputState, usePrevious } from "./hooks.js";
import { validateChildren, validateMaxLength } from "./validate-props.js";
import { defer } from "./utils/defer.js";
import { isInputFocused } from "./utils/input.js";
import { getElementDocument, isFunction, toString as helpers_js_toString } from "./utils/helpers.js";
import utils_mask from "./utils/mask.js";
import children_wrapper from "./children-wrapper.js";
function _define_property(obj, key, value) {
    if (key in obj) Object.defineProperty(obj, key, {
        value: value,
        enumerable: true,
        configurable: true,
        writable: true
    });
    else obj[key] = value;
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = null != arguments[i] ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if ("function" == typeof Object.getOwnPropertySymbols) ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
            return Object.getOwnPropertyDescriptor(source, sym).enumerable;
        }));
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ReactInputMask_ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) symbols = symbols.filter(function(sym) {
            return Object.getOwnPropertyDescriptor(object, sym).enumerable;
        });
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = null != source ? source : {};
    if (Object.getOwnPropertyDescriptors) Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    else ReactInputMask_ownKeys(Object(source)).forEach(function(key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
    return target;
}
function _object_without_properties(source, excluded) {
    if (null == source) return {};
    var target = {}, sourceKeys, key, i;
    if ("u" > typeof Reflect && Reflect.ownKeys) {
        sourceKeys = Reflect.ownKeys(Object(source));
        for(i = 0; i < sourceKeys.length; i++){
            key = sourceKeys[i];
            if (!(excluded.indexOf(key) >= 0)) {
                if (Object.prototype.propertyIsEnumerable.call(source, key)) target[key] = source[key];
            }
        }
        return target;
    }
    target = _object_without_properties_loose(source, excluded);
    if (Object.getOwnPropertySymbols) {
        sourceKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceKeys.length; i++){
            key = sourceKeys[i];
            if (!(excluded.indexOf(key) >= 0)) {
                if (Object.prototype.propertyIsEnumerable.call(source, key)) target[key] = source[key];
            }
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (null == source) return {};
    var target = {}, sourceKeys = Object.getOwnPropertyNames(source), key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (!(excluded.indexOf(key) >= 0)) {
            if (Object.prototype.propertyIsEnumerable.call(source, key)) target[key] = source[key];
        }
    }
    return target;
}
const ReactInputMask_InputMask = /*#__PURE__*/ forwardRef(function(props, forwardedRef) {
    const $ = c(27);
    let beforeMaskedStateChange;
    let children;
    let mask;
    let restProps;
    let t0;
    let t1;
    if ($[0] !== props) {
        const { alwaysShowMask: t2, children: t3, mask: t4, maskPlaceholder: t5, beforeMaskedStateChange: t6, formatChars } = props, t7 = _object_without_properties(props, [
            "alwaysShowMask",
            "children",
            "mask",
            "maskPlaceholder",
            "beforeMaskedStateChange",
            "formatChars"
        ]);
        t0 = t2;
        children = t3;
        mask = t4;
        t1 = t5;
        beforeMaskedStateChange = t6;
        restProps = t7;
        $[0] = props;
        $[1] = beforeMaskedStateChange;
        $[2] = children;
        $[3] = mask;
        $[4] = restProps;
        $[5] = t0;
        $[6] = t1;
    } else {
        beforeMaskedStateChange = $[1];
        children = $[2];
        mask = $[3];
        restProps = $[4];
        t0 = $[5];
        t1 = $[6];
    }
    const alwaysShowMask = void 0 === t0 ? false : t0;
    const maskPlaceholder = void 0 === t1 ? "_" : t1;
    validateMaxLength(props);
    const maskUtils = new utils_mask({
        mask,
        maskPlaceholder
    });
    const isMasked = !!mask;
    const isEditable = !restProps.disabled && !restProps.readOnly;
    const isControlled = null !== props.value && void 0 !== props.value;
    const previousIsMasked = usePrevious(isMasked);
    const t2 = (isControlled ? props.value : props.defaultValue) || "";
    let t3;
    if ($[7] !== t2) {
        t3 = helpers_js_toString(t2);
        $[7] = t2;
        $[8] = t3;
    } else t3 = $[8];
    const initialValue = t3;
    const { inputRef, getInputState, setInputState, getLastInputState } = useInputState(initialValue, isMasked);
    const getInputElement = useInputElement(inputRef);
    const onChange = function(event) {
        const currentState = getInputState();
        const previousState = getLastInputState();
        let newInputState = maskUtils.processChange(currentState, previousState);
        if (beforeMaskedStateChange) newInputState = beforeMaskedStateChange({
            currentState,
            previousState,
            nextState: newInputState
        });
        setInputState(newInputState);
        if (props.onChange) props.onChange(event);
    };
    const onFocus = function(event_0) {
        inputRef.current = event_0.target;
        const currentValue = getInputState().value;
        if (isMasked && !maskUtils.isValueFilled(currentValue)) {
            let newValue = maskUtils.formatValue(currentValue);
            const newSelection = maskUtils.getDefaultSelectionForValue(newValue);
            let newInputState_0 = {
                value: newValue,
                selection: newSelection
            };
            if (beforeMaskedStateChange) {
                newInputState_0 = beforeMaskedStateChange({
                    currentState: getInputState(),
                    nextState: newInputState_0
                });
                newValue = newInputState_0.value;
            }
            setInputState(newInputState_0);
            if (newValue !== currentValue && props.onChange) props.onChange(event_0);
            defer(()=>{
                setInputState(getLastInputState());
            });
        }
        if (props.onFocus) props.onFocus(event_0);
    };
    const onBlur = function(event_1) {
        const currentValue_0 = getInputState().value;
        const lastValue = getLastInputState().value;
        if (isMasked && !alwaysShowMask && maskUtils.isValueEmpty(lastValue)) {
            let newValue_0 = "";
            let newInputState_1 = {
                value: "",
                selection: {
                    start: null,
                    end: null
                }
            };
            if (beforeMaskedStateChange) {
                newInputState_1 = beforeMaskedStateChange({
                    currentState: getInputState(),
                    nextState: newInputState_1
                });
                newValue_0 = newInputState_1.value;
            }
            setInputState(newInputState_1);
            if (newValue_0 !== currentValue_0 && props.onChange) props.onChange(event_1);
        }
        if (props.onBlur) props.onBlur(event_1);
    };
    const onMouseDown = function(event_2) {
        const input = getInputElement();
        const { value } = getInputState();
        const inputDocument = getElementDocument(input);
        if (!isInputFocused(input) && !maskUtils.isValueFilled(value)) {
            const mouseDownX = event_2.clientX;
            const mouseDownY = event_2.clientY;
            const mouseDownTime = new Date().getTime();
            const mouseUpHandler = (mouseUpEvent)=>{
                null == inputDocument || inputDocument.removeEventListener("mouseup", mouseUpHandler);
                if (!isInputFocused(input)) return;
                const deltaX = Math.abs(mouseUpEvent.clientX - mouseDownX);
                const deltaY = Math.abs(mouseUpEvent.clientY - mouseDownY);
                const axisDelta = Math.max(deltaX, deltaY);
                const timeDelta = new Date().getTime() - mouseDownTime;
                if (axisDelta <= 10 && timeDelta <= 200 || axisDelta <= 5 && timeDelta <= 300) {
                    const lastState = getLastInputState();
                    const newSelection_0 = maskUtils.getDefaultSelectionForValue(lastState.value);
                    const newState = _object_spread_props(_object_spread({}, lastState), {
                        selection: newSelection_0
                    });
                    setInputState(newState);
                }
            };
            null == inputDocument || inputDocument.addEventListener("mouseup", mouseUpHandler);
        }
        if (props.onMouseDown) props.onMouseDown(event_2);
    };
    if (isMasked && isControlled) {
        const input_0 = getInputElement();
        const isFocused = input_0 && isInputFocused(input_0);
        let newValue_1 = isFocused || alwaysShowMask || props.value ? maskUtils.formatValue(props.value || "") : props.value || "";
        if (beforeMaskedStateChange) newValue_1 = beforeMaskedStateChange({
            nextState: {
                value: newValue_1,
                selection: {
                    start: null,
                    end: null
                }
            }
        }).value;
        setInputState(_object_spread_props(_object_spread({}, getLastInputState()), {
            value: newValue_1
        }));
    }
    let t4;
    if ($[9] !== getLastInputState) {
        t4 = getLastInputState();
        $[9] = getLastInputState;
        $[10] = t4;
    } else t4 = $[10];
    const lastState_0 = t4;
    const lastSelection = lastState_0.selection;
    const lastValue_0 = lastState_0.value;
    useLayoutEffect(()=>{
        if (!isMasked) return;
        const input_1 = getInputElement();
        const isFocused_0 = isInputFocused(input_1);
        const previousSelection = lastSelection;
        const currentState_0 = getInputState();
        let newInputState_2 = _object_spread({}, currentState_0);
        if (!isControlled) {
            const currentValue_1 = currentState_0.value;
            const formattedValue = maskUtils.formatValue(currentValue_1);
            const isValueEmpty = maskUtils.isValueEmpty(formattedValue);
            const shouldFormatValue = !isValueEmpty || isFocused_0 || alwaysShowMask;
            if (shouldFormatValue) newInputState_2.value = formattedValue;
            else if (isValueEmpty && !isFocused_0) newInputState_2.value = "";
        }
        if (isFocused_0 && !previousIsMasked) newInputState_2.selection = maskUtils.getDefaultSelectionForValue(newInputState_2.value);
        else if (isControlled && isFocused_0 && previousSelection) {
            if (null !== previousSelection.start && null !== previousSelection.end) newInputState_2.selection = previousSelection;
        }
        if (beforeMaskedStateChange) newInputState_2 = beforeMaskedStateChange({
            currentState: currentState_0,
            nextState: newInputState_2
        });
        setInputState(newInputState_2);
    });
    const t5 = isMasked && isEditable ? onChange : props.onChange;
    const t6 = isMasked && isEditable ? onMouseDown : props.onMouseDown;
    let t7;
    if ($[11] !== forwardedRef || $[12] !== inputRef) {
        t7 = (ref)=>{
            inputRef.current = ref;
            if (isFunction(forwardedRef)) forwardedRef(ref);
            else if (null !== forwardedRef && "object" == typeof forwardedRef) forwardedRef.current = ref;
        };
        $[11] = forwardedRef;
        $[12] = inputRef;
        $[13] = t7;
    } else t7 = $[13];
    const t8 = isMasked && isControlled ? lastValue_0 : props.value;
    let t9;
    if ($[14] !== onBlur || $[15] !== onFocus || $[16] !== restProps || $[17] !== t5 || $[18] !== t6 || $[19] !== t7 || $[20] !== t8) {
        t9 = _object_spread_props(_object_spread({}, restProps), {
            onFocus,
            onBlur,
            onChange: t5,
            onMouseDown: t6,
            ref: t7,
            value: t8
        });
        $[14] = onBlur;
        $[15] = onFocus;
        $[16] = restProps;
        $[17] = t5;
        $[18] = t6;
        $[19] = t7;
        $[20] = t8;
        $[21] = t9;
    } else t9 = $[21];
    const inputProps = t9;
    if (children) {
        validateChildren(props, children);
        let t10;
        if ($[22] !== children || $[23] !== inputProps) {
            t10 = /*#__PURE__*/ jsx(children_wrapper, _object_spread_props(_object_spread({}, inputProps), {
                children: children
            }));
            $[22] = children;
            $[23] = inputProps;
            $[24] = t10;
        } else t10 = $[24];
        return t10;
    }
    let t10;
    if ($[25] !== inputProps) {
        t10 = /*#__PURE__*/ jsx("input", _object_spread({}, inputProps));
        $[25] = inputProps;
        $[26] = t10;
    } else t10 = $[26];
    return t10;
});
ReactInputMask_InputMask.displayName = 'InputMask';
const ReactInputMask = ReactInputMask_InputMask;
export default ReactInputMask;
